/**
 * @license
 *
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Blockly React Component.
 * @author samelh@google.com (Sam El-Husseini)
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './BlocklyComponent.css'

import * as Blockly from 'blockly/core'
import {
  ContinuousToolbox,
  ContinuousFlyout,
  ContinuousMetrics,
  // @ts-expect-error Types are not available for this package.
} from './continuousToolbox'
import classNames from 'classnames'
import config from './config'
import { useTranslator } from '../translator/hook'
import 'blockly/blocks'
import {
  registerCallbacks,
  RequestVarName,
  RequestVarNameType,
} from '../../blocks/blocks'
import { messages } from '../../blocks/messages'
import { useProject } from '../project/hook'
import { TabBar } from '../tabBar/tabBar'
import { BackButton } from '../buttons/backButton'
import { ForwardButton } from '../buttons/forwardButton'
import { SmallButtonLua } from '../buttons/smallButtonLua'
import { VariableModal } from '../modals/variableModal'

// Disable the inline block context menu item.
Blockly.ContextMenuRegistry.registry.unregister('blockInline')

interface BlocklyComponentProps {
  injectionOptions?: Blockly.BlocklyOptions
}

function BlocklyComponent({ injectionOptions }: BlocklyComponentProps) {
  const { canEdit, missionManager, projectId } = useProject()
  const { language, setLanguage } = useTranslator()
  const blocklyDivs = useRef<Record<string, HTMLDivElement>>({})
  const workspaces = useRef<Record<string, Blockly.WorkspaceSvg>>({})
  const unregisterCallbacks = useRef<Array<() => void>>([])
  const [varNameEvent, setVarNameEvent] = useState<RequestVarName | null>(null)

  useEffect(() => {
    Object.values(workspaces.current).forEach((workspace) => {
      workspace.hideChaff()
    })
    if (
      missionManager.current &&
      blocklyDivs.current[missionManager.current.name] &&
      !workspaces.current[missionManager.current.name]
    ) {
      const injectionDiv = blocklyDivs.current[missionManager.current.name]
      const currentWorkspace = Blockly.inject(injectionDiv, {
        ...config,
        plugins: {
          flyoutsVerticalToolbox: ContinuousFlyout,
          metricsManager: ContinuousMetrics,
          toolbox: ContinuousToolbox,
        },
        readOnly: !canEdit,
        ...injectionOptions,
      })
      unregisterCallbacks.current.push(registerCallbacks(currentWorkspace))
      workspaces.current[missionManager.current.name] = currentWorkspace
      currentWorkspace.scrollCenter()
      currentWorkspace.addChangeListener((event) => {
        if (
          event.type === RequestVarNameType.CREATE ||
          event.type === RequestVarNameType.RENAME
        ) {
          setVarNameEvent(event as RequestVarName)
        }
      })
    }
  }, [canEdit, injectionOptions, missionManager])

  useEffect(
    () => () => {
      // Clean up callbacks, workspaces, and messages on unmount.
      unregisterCallbacks.current.forEach((unregister) => unregister())
      unregisterCallbacks.current = []
      for (const workspace of Object.values(workspaces.current)) {
        // Sometimes, the workspace is somehow not subscribed to the theme manager,
        // and disposing it will cause an error. Make sure it's subscribed first.
        const themeManager = workspace.getThemeManager()
        // @ts-expect-error subscribedWorkspaces is private.
        if (!themeManager.subscribedWorkspaces_?.includes(workspace)) {
          themeManager.subscribeWorkspace(workspace)
        }
        workspace.dispose()
      }
      workspaces.current = {}
      messages.reset()
    },
    // We want to clean up when the component unmounts or the project ID changes.
    // Also, if the user's ability to edit changes, we have to re-make the workspaces.
    [canEdit, projectId],
  )

  useEffect(() => {
    // When the language changes, update all the workspaces.
    for (const workspace of Object.values(workspaces.current)) {
      setLanguage(language, workspace)
    }
  }, [language, setLanguage])

  const toolbarElem = document.getElementById('blockly-toolbar')

  return (
    <>
      {toolbarElem &&
        createPortal(
          <>
            <BackButton
              onClick={() => {
                if (missionManager?.current?.name) {
                  const workspace =
                    workspaces.current[missionManager.current.name]
                  if (workspace) {
                    try {
                      workspace.undo(false)
                    } catch (error) {
                      console.warn('Error during undo operation:', error)
                      workspace.clearUndo()
                    }
                  }
                }
              }}
            />
            <ForwardButton
              onClick={() => {
                if (missionManager?.current?.name) {
                  const workspace =
                    workspaces.current[missionManager.current.name]
                  if (workspace) {
                    try {
                      workspace.undo(true)
                    } catch (error) {
                      console.warn('Error during redo operation:', error)
                      workspace.clearUndo()
                    }
                  }
                }
              }}
            />
          </>,
          toolbarElem,
        )}
      <TabBar>
        <SmallButtonLua />
      </TabBar>
      <VariableModal
        event={varNameEvent}
        onClose={() => setVarNameEvent(null)}
      />
      {missionManager.objectNames.map((name) => (
        <div
          key={name}
          ref={(elem) => {
            if (elem) blocklyDivs.current[name] = elem
            else delete blocklyDivs.current[name]
          }}
          className={classNames('blockly-div', {
            'blockly-div-hidden': missionManager.current?.name !== name,
          })}
        />
      ))}
    </>
  )
}

export default BlocklyComponent
